import { useState } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { X, Loader2, Upload, Download, FileSpreadsheet, ArrowLeft, CheckCircle2, ChevronDown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CONNECTOR_META } from "./shared";

const TEMPLATE = `job_title,requestor,sla_hours,sla_minutes,sla_policy,app_name,connector,action,target,requires_approval,requires_mfa,approver
"Onboard — J. Doe (INC1001)",jdoe@wemabank.com,,45,ticket,Core Banking (Finacle),azure_ad,grant,Finacle-Teller,true,false,branchhead@wemabank.com
"Onboard — J. Doe (INC1001)",jdoe@wemabank.com,,45,ticket,Mainframe GL,rpa,provision,GL-Ops,false,true,
"Onboard — A. Bello (INC1002)",abello@wemabank.com,8,,ticket,Loan Origination,rest_api,grant,loan-officer,true,true,credithead@wemabank.com`;

const FIELDS = [
  { key: "job_title", label: "Job title", req: true },
  { key: "requestor", label: "Requestor" },
  { key: "sla_hours", label: "SLA (hours)" },
  { key: "sla_minutes", label: "SLA (minutes)" },
  { key: "sla_policy", label: "SLA policy" },
  { key: "app_name", label: "Application name", req: true },
  { key: "connector", label: "Connector" },
  { key: "action", label: "Action" },
  { key: "target", label: "Target" },
  { key: "requires_approval", label: "Requires approval" },
  { key: "requires_mfa", label: "Requires MFA" },
  { key: "approver", label: "Approver" },
];

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

function autoMap(headers) {
  const m = {};
  FIELDS.forEach((f) => {
    const hit = headers.find((h) => norm(h) === norm(f.key) || norm(h) === norm(f.label));
    if (hit) m[f.key] = hit;
  });
  return m;
}

function slaText(j) {
  if (j.sla_minutes && j.sla_minutes < 60) return `${j.sla_minutes}m SLA`;
  if (j.sla_minutes) return `${Math.round((j.sla_minutes / 60) * 10) / 10}h SLA`;
  return `${j.sla_hours || 24}h SLA`;
}

export default function UploadJobsModal({ onClose, onCreated }) {
  const [step, setStep] = useState("file"); // file | map | preview | committing | done
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, tasks: 0 });
  const [openJob, setOpenJob] = useState(null);
  const [commitErrors, setCommitErrors] = useState([]);

  const downloadTemplate = () => {
    const url = window.URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "orchestration_jobs_template.csv";
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const onFile = (file) => {
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: "greedy",
      complete: (res) => {
        const flds = (res.meta.fields || []).filter(Boolean);
        if (!flds.length || !res.data.length) { toast.error("Could not read any rows from that file."); return; }
        setHeaders(flds); setRows(res.data); setMapping(autoMap(flds)); setStep("map");
      },
      error: () => toast.error("Failed to parse the CSV file."),
    });
  };

  const buildPreview = async () => {
    if (!mapping.job_title || !mapping.app_name) { toast.error("Map both Job title and Application name first."); return; }
    const cols = FIELDS.filter((f) => mapping[f.key]).map((f) => f.key);
    const data = rows.map((r) => {
      const o = {};
      cols.forEach((k) => { o[k] = r[mapping[k]] ?? ""; });
      return o;
    });
    const csv = Papa.unparse({ fields: cols, data });
    setBusy(true);
    try {
      const { data: res } = await api.post("/orchestration/jobs/upload/preview", csv, { headers: { "Content-Type": "text/csv" } });
      if (!res.jobs_count) { toast.error("No valid jobs found. Check your column mapping."); setBusy(false); return; }
      setPreview(res); setStep("preview");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setStep("committing"); setCommitErrors([]);
    const jobs = preview.jobs;
    setProgress({ done: 0, total: jobs.length, tasks: 0 });
    let tasks = 0; const errs = [];
    for (let i = 0; i < jobs.length; i++) {
      const j = jobs[i];
      try {
        await api.post("/orchestration/jobs", {
          title: j.title, requestor: j.requestor, sla_hours: j.sla_hours,
          sla_minutes: j.sla_minutes, sla_policy: j.sla_policy, tasks: j.tasks, source: "upload",
        });
        tasks += j.tasks.length;
      } catch (e) { errs.push(`${j.title}: ${apiErr(e)}`); }
      setProgress({ done: i + 1, total: jobs.length, tasks });
    }
    setCommitErrors(errs);
    setStep("done");
    onCreated();
    if (errs.length) toast.error(`${errs.length} job(s) failed to create.`);
    else toast.success(`${jobs.length} job(s), ${tasks} task(s) created.`);
  };

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="upload-jobs-modal">
      <div className="bg-[var(--parchment)] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="brand-band text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="font-semibold">Bulk upload jobs (CSV)</p>
            <p className="text-xs text-white/70 mt-0.5">
              {step === "file" && "Step 1 of 3 — choose file"}
              {step === "map" && "Step 2 of 3 — map columns"}
              {step === "preview" && "Step 3 of 3 — review before creating"}
              {(step === "committing" || step === "done") && "Creating jobs"}
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* STEP 1 — FILE */}
          {step === "file" && (
            <>
              <p className="text-sm text-neutral-500">One row per application task. Rows sharing the same <b>job title</b> are grouped into a single job. Any spreadsheet works — you'll map its columns in the next step.</p>
              <button data-testid="download-template-btn" onClick={downloadTemplate} className="btn btn-outline text-xs">
                <Download className="w-4 h-4" /> Download CSV template
              </button>
              <div>
                <label className="text-xs font-semibold text-neutral-600">CSV file</label>
                <input data-testid="upload-file-input" type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files[0])} className="field-input mt-1 !py-2" />
              </div>
            </>
          )}

          {/* STEP 2 — MAP */}
          {step === "map" && (
            <>
              <p className="text-sm text-neutral-500">Match your spreadsheet's columns to the fields below. We auto-matched what we could — adjust anything that's off. <span className="text-red-600">*</span> fields are required.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {FIELDS.map((f) => (
                  <div key={f.key} className="flex items-center justify-between gap-2">
                    <label className="text-xs font-medium text-neutral-700 whitespace-nowrap">{f.label}{f.req && <span className="text-red-600"> *</span>}</label>
                    <select data-testid={`map-field-${f.key}`} value={mapping[f.key] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))} className="field-input !py-1.5 !w-40 text-xs">
                      <option value="">— none —</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-400">{rows.length} data row(s) detected.</p>
              <div className="flex justify-between gap-2 pt-1">
                <button data-testid="upload-back-btn" onClick={() => setStep("file")} className="btn btn-outline text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                <button data-testid="upload-preview-btn" onClick={buildPreview} disabled={busy} className="btn btn-primary">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Preview
                </button>
              </div>
            </>
          )}

          {/* STEP 3 — PREVIEW (dry run) */}
          {step === "preview" && preview && (
            <div data-testid="upload-preview">
              <div className="flex gap-3 mb-3">
                <div className="card-surface p-3 flex-1 text-center"><p className="text-2xl font-bold text-[var(--burgundy)]">{preview.jobs_count}</p><p className="text-xs text-neutral-500">job(s)</p></div>
                <div className="card-surface p-3 flex-1 text-center"><p className="text-2xl font-bold text-[var(--burgundy)]">{preview.tasks_count}</p><p className="text-xs text-neutral-500">task(s)</p></div>
                <div className="card-surface p-3 flex-1 text-center"><p className={`text-2xl font-bold ${preview.errors.length ? "text-red-600" : "text-green-700"}`}>{preview.errors.length}</p><p className="text-xs text-neutral-500">row(s) skipped</p></div>
              </div>
              <p className="text-xs text-neutral-400 mb-2">Nothing has been created yet — this is a dry run. Expand a job to inspect its tasks.</p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {preview.jobs.map((j, i) => (
                  <div key={i} className="border border-[hsl(var(--border))] rounded-lg bg-white/60">
                    <button data-testid={`preview-job-${i}`} onClick={() => setOpenJob(openJob === i ? null : i)} className="w-full flex items-center justify-between p-2.5 text-left">
                      <span className="text-sm font-medium text-neutral-800 truncate">{j.title}</span>
                      <span className="flex items-center gap-2 text-xs text-neutral-500 whitespace-nowrap">{j.tasks.length} app(s) · {slaText(j)} <ChevronDown className={`w-4 h-4 transition-transform ${openJob === i ? "rotate-180" : ""}`} /></span>
                    </button>
                    {openJob === i && (
                      <div className="px-2.5 pb-2.5 space-y-1">
                        {j.requestor && <p className="text-xs text-neutral-500">Requestor: {j.requestor}</p>}
                        {j.tasks.map((t, ti) => (
                          <div key={ti} className="flex items-center gap-2 text-xs text-neutral-600 border-t border-dashed pt-1">
                            <span className={`px-1.5 py-0.5 rounded ${CONNECTOR_META[t.connector]?.cls || ""}`}>{CONNECTOR_META[t.connector]?.label || t.connector}</span>
                            <span className="flex-1 truncate">{t.app_name}{t.target ? ` → ${t.target}` : ""}</span>
                            {t.requires_approval && <span className="text-indigo-700">appr</span>}
                            {t.requires_mfa && <span className="text-orange-700">mfa</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {preview.errors.length > 0 && (
                <div className="mt-2 text-xs text-red-600 max-h-24 overflow-y-auto">
                  <p className="font-semibold">{preview.errors.length} row(s) will be skipped:</p>
                  {preview.errors.slice(0, 20).map((e, i) => <p key={i}>• {e}</p>)}
                </div>
              )}
              <div className="flex justify-between gap-2 pt-3">
                <button data-testid="upload-back-btn" onClick={() => setStep("map")} className="btn btn-outline text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                <button data-testid="upload-commit-btn" onClick={commit} className="btn btn-primary"><Upload className="w-4 h-4" /> Create {preview.jobs_count} job(s)</button>
              </div>
            </div>
          )}

          {/* COMMITTING */}
          {step === "committing" && (
            <div data-testid="upload-progress" className="py-6 text-center space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--burgundy)] mx-auto" />
              <p className="text-sm font-medium text-neutral-700">Provisioning job {progress.done} of {progress.total} · {progress.tasks} task(s) created</p>
              <div className="w-full h-2.5 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-[var(--burgundy)] transition-all duration-300" style={{ width: `${pct}%` }} data-testid="upload-progress-bar" />
              </div>
              <p className="text-xs text-neutral-400">{pct}%</p>
            </div>
          )}

          {/* DONE */}
          {step === "done" && (
            <div data-testid="upload-done" className="py-6 text-center space-y-3">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
              <p className="text-sm font-semibold text-neutral-800">{progress.done - commitErrors.length} job(s), {progress.tasks} task(s) created</p>
              {commitErrors.length > 0 && (
                <div className="text-xs text-red-600 text-left max-h-32 overflow-y-auto">
                  {commitErrors.map((e, i) => <p key={i}>• {e}</p>)}
                </div>
              )}
              <button onClick={onClose} className="btn btn-primary">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
